import { Message } from "@apis:message/message";
import { Box, Link } from "@mui/material";
import { ReferenceContentCell } from "@pages:app/talk/shared/ReferenceContentCell";
import {
  CustomizedTables,
  StyledTableCell,
} from "@pages:app/talk/shared/Tables";
import { groupReferences } from "@pages:app/talk/shared/utils";
import { isURL } from "@utils:general";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

interface ReferenceProps {
  references: Message["GET_ONE"]["Response"]["docs"];
  showContent?: boolean;
}

const headers = (showContent: boolean, t: TFunction) => {
  const baseHeaders = [
    {
      key: "searchRanking",
      value: t("chat.reference.ref"),
      width: "5%",
    },
    {
      key: "title",
      value: t("chat.reference.document"),
      width: showContent ? "30%" : "95%",
      render: (row: Record<string, unknown>) => {
        return isURL(row?.reference as string) ? (
          <Link
            href={row?.reference as string}
            target="_blank"
            rel="noreferrer"
          >
            {row?.title as string}
          </Link>
        ) : (
          <StyledTableCell>{row?.title as string}</StyledTableCell>
        );
      },
    },
  ];

  if (!showContent) return baseHeaders;
  return [
    ...baseHeaders,
    {
      key: "content",
      value: t("chat.reference.content"),
      width: "65%",
      render: (row: Record<string, unknown>) => (
        <ReferenceContentCell
          content={row.content as string}
          fullContent={row.fullContent as string}
          highlightSpans={
            row.highlightSpans as { start: number; end: number }[]
          }
          fullHighlightSpans={
            row.fullHighlightSpans as { start: number; end: number }[]
          }
          maxLength={150}
        />
      ),
    },
  ];
};

export const Reference = ({
  references,
  showContent = true,
}: ReferenceProps) => {
  const { t } = useTranslation();
  const referencesWithRef = groupReferences(references);

  if (!Array.isArray(references) || references.length === 0) return null;

  return (
    <Box>
      <CustomizedTables
        rows={referencesWithRef}
        headers={headers(showContent, t)}
        sx={{
          border: "none",
          "& .MuiTableCell-head": {
            padding: 1,
            fontWeight: "500",
            fontSize: "16px",
            lineHeight: "26px",
            letterSpacing: "0.15px",
            fontFamily: "Plus Jakarta Sans Variable, sans-serif",
          },
          "& .MuiTableCell-body": {
            padding: 1,
            fontWeight: "400",
            fontSize: "16px",
            lineHeight: "31px",
            letterSpacing: "0.15px",
            fontFamily: "Plus Jakarta Sans Variable, sans-serif",
            wordBreak: "break-word",
          },
          marginTop: "0 !important",
          marginBottom: "0 !important",
        }}
      />
    </Box>
  );
};
